// Updated CourseImageGallery component for Django backend
import React, { useEffect, useState } from 'react';
import './CourseImageGallery.css';

const CourseImageGallery = ({
  courseName,
  location,
  imageCount = 4,
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!courseName) return;

    const fetchImages = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          courseName,
          imageCount: imageCount.toString()
        });

        if (location) {
          params.append('location', location);
        }

        // Use your Django API endpoint
        const response = await fetch(`https://lionfish-app-w73zu.ondigitalocean.app/api/course-images/?${params.toString()}`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `API error: ${response.status}`);
        }

        const data = await response.json();

        // Limit to the requested count on frontend
        const limitedImages = (data.images || []).slice(0, imageCount);
        setImages(limitedImages);

        console.log(`API returned ${data.images?.length || 0} images, showing ${limitedImages.length} for ${courseName}`);
        if (data.cached) {
          console.log('Results served from cache');
        }

      } catch (err) {
        console.error('Error fetching images:', err);
        setError(err.message || 'Failed to fetch images');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [courseName, location, imageCount]);

  // Rest of your component remains the same...
  const openImageModal = (img, idx) => {
    setSelectedImage(img);
    setCurrentIndex(idx);
  };

  const closeImageModal = () => setSelectedImage(null);

  const handlePrev = (e) => {
    e.stopPropagation();
    const i = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(i);
    setSelectedImage(images[i]);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    const i = (currentIndex + 1) % images.length;
    setCurrentIndex(i);
    setSelectedImage(images[i]);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedImage) return;
      if (e.key === 'ArrowLeft') handlePrev(e);
      if (e.key === 'ArrowRight') handleNext(e);
      if (e.key === 'Escape') closeImageModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedImage, currentIndex, images]);

  const handleImageError = (e, fallbackSrc) => {
    if (fallbackSrc && e.target.src !== fallbackSrc) {
      e.target.src = fallbackSrc;
    } else {
      e.target.style.display = 'none';
    }
  };

  if (loading) return <div className="image-gallery-loading">Loading course images...</div>;

  if (error) {
    return (
      <div className="image-gallery-error">
        <p>Unable to load images</p>
        <small>{error}</small>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="image-gallery-empty">
        <p>No images found for {courseName}</p>
        <small>Try searching manually to verify availability</small>
      </div>
    );
  }

  return (
    <div className="course-image-gallery">
      <div className="gallery-grid">
        {images.slice(0, imageCount).map((img, idx) => (
          <div key={idx} className="gallery-item">
            <img
              src={img.properties.url}
              alt={`${courseName} ${idx + 1}`}
              onClick={() => openImageModal(img, idx)}
              className="gallery-thumbnail"
              loading="lazy"
              onLoad={(e) => e.target.classList.add('loaded')}
              onError={(e) => handleImageError(e, img.thumbnail?.src)}
            />
          </div>
        ))}
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <span className="close-button" onClick={closeImageModal}>&times;</span>
            <div className="nav-arrow prev-arrow" onClick={handlePrev}>&#10094;</div>
            <div className="nav-arrow next-arrow" onClick={handleNext}>&#10095;</div>
            <img
              src={selectedImage.properties?.url || selectedImage.thumbnail.src}
              alt={selectedImage.title || 'Course image'}
              className="modal-image"
              onError={(e) => handleImageError(e, selectedImage.thumbnail?.src)}
            />
            <div className="image-metadata">
              <div className="image-title">{selectedImage.title}</div>
              <div className="image-source">
                Source: <a
                  href={selectedImage.contextLink || `https://${selectedImage.source}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  {selectedImage.source || 'Unknown'}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseImageGallery;